
import { useState, useEffect } from 'react';
import { useSearchParams } from 'react-router-dom';
import { supabase } from '@/integrations/supabase/client';
import { ExtendedProfessionalEntry, ProfessionalFilters } from '@/types/professional';

export function useProfessionalDirectoryQuery() {
  const [searchParams, setSearchParams] = useSearchParams();

  // Core search and location state
  const [search, setSearch] = useState(searchParams.get('search') || '');
  const [location, setLocation] = useState(searchParams.get('location') || '');
  const [radius, setRadius] = useState(Number(searchParams.get('radius')) || 25);
  const [licenseVerifiedOnly, setLicenseVerifiedOnly] = useState(searchParams.get('verified') === 'true');
  const [sortBy, setSortBy] = useState(searchParams.get('sort') || 'best_match');

  // Professional-specific filters
  const [filters, setFilters] = useState<ProfessionalFilters>({
    licenseVerificationStatus: (searchParams.get('licenseStatus') as any) || 'all',
    professionalRole: (searchParams.get('role') as any) || 'all',
    specialties: searchParams.get('specialties')?.split(',').map(s => s.replace(/:\s*\d[\d,]*/g, '')).filter(Boolean) || [],
    consultationModes: (searchParams.get('consultationModes')?.split(',').filter(Boolean) as any[]) || [],
    insuranceAccepted: searchParams.get('insurance')?.split(',').filter(Boolean) || [],
    experienceYearsMin: searchParams.get('expMin') ? Number(searchParams.get('expMin')) : undefined,
    experienceYearsMax: searchParams.get('expMax') ? Number(searchParams.get('expMax')) : undefined,
    serviceRadius: searchParams.get('serviceRadius') ? Number(searchParams.get('serviceRadius')) : undefined,
    telehealthAvailable: searchParams.get('telehealth') === 'true',
    certificationBodies: searchParams.get('certifications')?.split(',').filter(Boolean) || [],
  });

  // Data fetching state
  const [results, setResults] = useState<ExtendedProfessionalEntry[]>([]);
  const [loading, setLoading] = useState(false);
  const [totalCount, setTotalCount] = useState(0);

  // Debounce search and location
  const debouncedSearch = useDebounce(search, 300);
  const debouncedLocation = useDebounce(location, 300);

  // Update URL params when state changes
  useEffect(() => {
    const params = new URLSearchParams();
    
    if (debouncedSearch) params.set('search', debouncedSearch);
    if (debouncedLocation) params.set('location', debouncedLocation);
    if (radius !== 25) params.set('radius', radius.toString());
    if (licenseVerifiedOnly) params.set('verified', 'true');
    if (sortBy !== 'best_match') params.set('sort', sortBy);
    
    // Professional filters
    if (filters.licenseVerificationStatus && filters.licenseVerificationStatus !== 'all') {
      params.set('licenseStatus', filters.licenseVerificationStatus);
    }
    if (filters.professionalRole && filters.professionalRole !== 'all') {
      params.set('role', filters.professionalRole);
    }
    if (filters.specialties?.length) params.set('specialties', filters.specialties.join(','));
    if (filters.consultationModes?.length) params.set('consultationModes', filters.consultationModes.join(','));
    if (filters.insuranceAccepted?.length) params.set('insurance', filters.insuranceAccepted.join(','));
    if (filters.experienceYearsMin !== undefined) params.set('expMin', filters.experienceYearsMin.toString());
    if (filters.experienceYearsMax !== undefined) params.set('expMax', filters.experienceYearsMax.toString());
    if (filters.serviceRadius !== undefined) params.set('serviceRadius', filters.serviceRadius.toString());
    if (filters.telehealthAvailable) params.set('telehealth', 'true');
    if (filters.certificationBodies?.length) params.set('certifications', filters.certificationBodies.join(','));

    setSearchParams(params);
  }, [
    debouncedSearch, debouncedLocation, radius, licenseVerifiedOnly, sortBy, filters, setSearchParams
  ]);

  // Fetch professionals
  useEffect(() => {
    const fetchProfessionals = async () => {
      setLoading(true);
      console.log('Fetching professionals with filters:', filters);
      try {
        // When a specific role is not selected, ask the API for "all" pros (the edge function will exclude caregivers)
        const professionParam = !filters.professionalRole || filters.professionalRole === 'all' ? 'all' : undefined;

        const requestBody = {
          search: debouncedSearch,
          location: debouncedLocation,
          radius,
          licenseVerifiedOnly,
          sortBy,
          excludeCaregivers: false, // Include caregivers in unified professional directory
          profession: professionParam,
          filters: {
            licenseVerificationStatus: filters.licenseVerificationStatus !== 'all' ? filters.licenseVerificationStatus : undefined,
            professionalRole: filters.professionalRole !== 'all' ? filters.professionalRole : undefined,
            specialties: filters.specialties?.length ? filters.specialties : undefined,
            consultationModes: filters.consultationModes?.length ? filters.consultationModes : undefined,
            insuranceAccepted: filters.insuranceAccepted?.length ? filters.insuranceAccepted : undefined,
            experienceYearsMin: filters.experienceYearsMin,
            experienceYearsMax: filters.experienceYearsMax,
            serviceRadius: filters.serviceRadius,
            telehealthAvailable: filters.telehealthAvailable || undefined,
            certificationBodies: filters.certificationBodies?.length ? filters.certificationBodies : undefined,
          }
        };
        console.log('API request body:', requestBody);
        
        const { data, error } = await supabase.functions.invoke('get-professional-directory', {
          body: requestBody
        });

        if (error) {
          console.error('Error fetching professionals:', error);
          setResults([]);
          setTotalCount(0);
        } else {
          const list = (data?.professionals as ExtendedProfessionalEntry[]) || [];
          setResults(list);
          setTotalCount(list.length);
        }
      } catch (error) {
        console.error('Error calling professional directory function:', error);
        setResults([]);
        setTotalCount(0);
      } finally {
        setLoading(false);
      }
    };

    fetchProfessionals();
  }, [debouncedSearch, debouncedLocation, radius, licenseVerifiedOnly, sortBy, filters]);

  const clearAllFilters = () => {
    setFilters({
      licenseVerificationStatus: 'all',
      professionalRole: 'all',
      specialties: [],
      consultationModes: [],
      insuranceAccepted: [],
      experienceYearsMin: undefined,
      experienceYearsMax: undefined,
      serviceRadius: undefined,
      telehealthAvailable: false,
      certificationBodies: [],
    });
    setSortBy('best_match');
  };

  return {
    // Search state
    search,
    setSearch,
    location,
    setLocation,
    radius,
    setRadius,
    licenseVerifiedOnly,
    setLicenseVerifiedOnly,
    sortBy,
    setSortBy,
    
    // Filters
    filters,
    setFilters,
    
    // Results
    results,
    loading,
    totalCount,
    clearAllFilters,
  };
}

function useDebounce<T>(value: T, delay = 300): T {
  const [debouncedValue, setDebouncedValue] = useState<T>(value);

  useEffect(() => {
    const handler = setTimeout(() => setDebouncedValue(value), delay);
    return () => clearTimeout(handler);
  }, [value, delay]);

  return debouncedValue;
}
